<template>
  <!--
    Details-Dialog einer Rechnung, geteilt von der Rechnungen-Seite
    (pages/billings/index.vue) und der Einrichtungs-Seite
    (pages/locations/location.vue). Maßstab ist das Verhalten der
    Einrichtungs-Seite: Speichern schreibt die Rechnung und erzeugt danach
    das PDF neu.

    Der Dialog bearbeitet eine KOPIE der Rechnung. Die Tabellenzeile der
    Seite ändert sich erst über die Events (saved, sent, pdf-created,
    billings), nicht schon beim Tippen.

    Öffnen: this.$refs.<ref>.open(invoice)
  -->
  <b-modal :id="modalId" title="Details" size="md" ok-only>
    <form class="forms-sample" @submit.prevent>
      <!-- #1116: storniertes Original. Ohne Backfill fehlen die Felder an
           alten Originalen, dann erscheint kein Hinweis (wie bisher). -->
      <b-alert v-if="state === 'cancelled'" variant="danger" show>
        {{ cancelledHint }}
      </b-alert>

      <!-- Kein zweiter Storno: der Server macht ihn fuer Beleg und
           storniertes Original zum No-op, der Knopf waere irrefuehrend. -->
      <div v-if="allowCancel && (state === 'open' || state === 'paid')" style="text-align: right">
        <div @click="cancelInvoice()" class="btn btn-link">
          Stornorechnung anlegen
        </div>
      </div>

      <b-form-group label="Rechnung verschickt am" label-for="">
        <date-picker
          v-model="current.emailsend_at"
          valueType="format"
          :format="dateFormat"
          placeholder="Datum wählen"
        ></date-picker>
      </b-form-group>

      <template v-if="allowSend">
        <b-form-group label="Empfänger" :label-for="modalId + '-receiver'">
          <b-form-input
            type="text"
            :id="modalId + '-receiver'"
            v-model="emailReceiver"
          ></b-form-input>
        </b-form-group>

        <div
          @click="sendInvoice()"
          class="btn btn-link"
          :class="{ disabled: sending }"
        >
          Rechnung senden
        </div>
        <b-alert v-if="sending" variant="primary" style="margin-top: 20px" show
          >Sende Rechnung ...</b-alert
        >
        <b-alert
          v-else-if="sendResult"
          :variant="sendResult.variant"
          style="margin-top: 20px"
          show
          >{{ sendResult.text }}</b-alert
        >
      </template>

      <b-form-group label="Bezahlt am" label-for="">
        <date-picker
          v-model="current.paid_at"
          valueType="format"
          :format="dateFormat"
          placeholder="Datum wählen"
        ></date-picker>
      </b-form-group>

      <b-form-group label="Rechnungsadresse" :label-for="modalId + '-address'">
        <b-form-textarea
          :id="modalId + '-address'"
          v-model="current.billing_address"
          placeholder="Rechnungsadresse"
          :rows="4"
          :max-rows="8"
        ></b-form-textarea>
      </b-form-group>

      <b-form-group label="Tags" :label-for="modalId + '-tags'">
        <b-form-tags
          :input-id="modalId + '-tags'"
          placeholder=""
          v-model="current.tags"
        ></b-form-tags>
      </b-form-group>

      <b-button
        variant="success"
        @click="save()"
        :disabled="saving"
        class="mr-2"
        >Speichern</b-button
      >

      <b-alert v-if="saving" variant="primary" style="margin-top: 20px" show
        >Speichere und erstelle PDF ...</b-alert
      >
      <b-alert
        v-if="saved && !saving"
        variant="primary"
        style="margin-top: 20px"
        show
        >Gespeichert</b-alert
      >
    </form>
  </b-modal>
</template>

<script>
import moment from 'moment';
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/locale/de';
import {
  updateInvoiceInfo,
  createPdf,
  cancelInvoice,
  sendInvoice
} from '@/services/locations.service';
import { invoiceState } from '@/utils/invoice';

// Texte zu data.reason der send-invoice-Antwort bei status "error";
// bei den PDF-Gründen hängt sendReasonText() den pdfFallbackHint an
const SEND_REASON_TEXTS = {
  no_pdf: 'Die Rechnung hat noch kein PDF.',
  pdf_download: 'Das PDF konnte nicht geladen werden.',
  mailjet:
    'Der Mailversand wurde abgelehnt. Bitte die Empfänger prüfen (mehrere durch Komma getrennt) und erneut senden.',
  not_found: 'Die Rechnung wurde nicht gefunden. Bitte die Seite neu laden.'
};
const PDF_REASONS = ['no_pdf', 'pdf_download'];

export default {
  name: 'InvoiceDetailsModal',
  components: {
    DatePicker
  },
  props: {
    modalId: {
      type: String,
      default: 'invoice-details-modal'
    },
    // "Stornorechnung anlegen" im Dialog anbieten
    allowCancel: {
      type: Boolean,
      default: true
    },
    // Empfängerfeld und "Rechnung senden" im Dialog anbieten
    allowSend: {
      type: Boolean,
      default: true
    },
    // Vorbelegung des Empfängerfelds beim Öffnen
    defaultReceiver: {
      type: String,
      default: ''
    },
    // Hinweis, wo das PDF von Hand neu erzeugt werden kann, falls das
    // automatische Neuerzeugen nach dem Speichern scheitert oder der Versand
    // am PDF scheitert
    pdfFallbackHint: {
      type: String,
      default: 'Bitte manuell über das PDF-Symbol neu erzeugen.'
    }
  },
  computed: {
    state() {
      return invoiceState(this.current);
    },
    cancelledHint() {
      const c = this.current || {};
      let text = 'Diese Rechnung wurde';
      if (c.cancelled_at) text += ' am ' + c.cancelled_at;
      if (c.cancelled_by_invoice_nr) text += ' durch RE-' + c.cancelled_by_invoice_nr;
      return text + ' storniert.';
    }
  },
  data() {
    return {
      dateFormat: 'DD.MM.YYYY',
      original: null,
      current: {},
      emailReceiver: '',
      sendResult: null,
      sending: false,
      saving: false,
      saved: false
    };
  },
  methods: {
    open(invoice) {
      this.original = invoice;
      const copy = Object.assign({}, invoice);
      copy.tags = Array.isArray(invoice.tags) ? invoice.tags.slice() : [];
      if (copy.emailsend_at === undefined) copy.emailsend_at = null;
      if (copy.paid_at === undefined) copy.paid_at = null;
      if (copy.billing_address === undefined) copy.billing_address = '';
      this.current = copy;
      this.emailReceiver = this.defaultReceiver || '';
      this.sendResult = null;
      this.sending = false;
      this.saving = false;
      this.saved = false;
      this.$bvModal.show(this.modalId);
    },

    save() {
      const item = this.current;
      const original = this.original;

      item.paid = !!item.paid_at;
      item.emailsend = !!item.emailsend_at;

      this.saving = true;
      this.saved = false;
      this.$emit('busy', true);

      let step = 'save';

      updateInvoiceInfo({ current_item: item })
        .then((data) => {
          const res = (data && data.data) || {};
          // Server meldet "nicht gefunden" mit status success und error im Body
          if (res.error) {
            return Promise.reject(res);
          }

          this.saved = true;
          this.$emit('saved', { original, invoice: item });
          if (res.billings) {
            this.$emit('billings', res.billings);
          }

          step = 'pdf';
          return createPdf(item);
        })
        .then((data) => {
          const res = (data && data.data) || {};
          this.$emit('pdf-created', {
            original,
            invoice: item,
            billings: res.billings || []
          });
          if (res.billings) {
            this.$emit('billings', res.billings);
          }
        })
        .catch((err) => {
          if (step === 'save') {
            this.saved = false;
            this.$toast.error(
              (err && err.message) || 'Rechnung konnte nicht gespeichert werden'
            );
          } else {
            this.$toast.warning(
              'Gespeichert, aber das PDF konnte nicht neu erzeugt werden. ' +
                this.pdfFallbackHint,
              { duration: 6000 }
            );
          }
        })
        .finally(() => {
          this.saving = false;
          this.$emit('busy', false);
        });
    },

    cancelInvoice() {
      const item = this.current;
      this.$swal({
        title: '',
        text: 'Soll eine Stornorechnung angelegt werden?',
        showCancelButton: true,
        confirmButtonColor: '#2563eb',
        cancelButtonColor: '#64748b',
        cancelButtonText: 'Abbrechen',
        confirmButtonText: 'Ja, anlegen.'
      }).then((result) => {
        if (!result.value) return;

        cancelInvoice({
          id: item._id,
          location_uid: item.location_uid
        }).then((data) => {
          const billings = data && data.data && data.data.billings;
          if (billings) {
            this.$emit('billings', billings);
            // #1116: Kennzeichnung des Originals in den offenen Dialog
            // uebernehmen, damit Hinweis erscheint und der Storno-Link
            // verschwindet
            const fresh = billings.find((b) => b && b._id === item._id);
            if (fresh && fresh.cancelled === true) {
              this.$set(item, 'cancelled', true);
              this.$set(item, 'cancelled_at', fresh.cancelled_at);
              this.$set(item, 'cancelled_by_invoice_nr', fresh.cancelled_by_invoice_nr);
            }
          } else {
            this.$toast.error('Stornorechnung konnte nicht angelegt werden.');
          }
        }).catch(() => {
          this.$toast.error('Stornorechnung konnte nicht angelegt werden.');
        });
      });
    },

    // Unbekannte Gründe liefern '' (nur "Rechnung nicht versendet.")
    sendReasonText(reason) {
      const text = SEND_REASON_TEXTS[reason];
      if (!text) return '';
      return PDF_REASONS.includes(reason)
        ? text + ' ' + this.pdfFallbackHint
        : text;
    },

    sendInvoice() {
      if (this.sending) return;
      const item = this.current;
      this.sending = true;
      this.sendResult = null;

      sendInvoice({
        email: this.emailReceiver,
        id: item._id,
        location_uid: item.location_uid
      })
        .then((data) => {
          const res = (data && data.data) || {};
          let result;

          if (res.status === 'ok') {
            result = { variant: 'primary', text: 'E-Mail gesendet' };

            // #879: Der Server setzt "Rechnung verschickt am" beim Versand. Auf
            // der Kopie im Dialog spiegeln, damit ein späteres "Speichern" den
            // Wert nicht mit dem alten (leeren) Stand überschreibt; die Seite
            // spiegelt ihn über das Event auf ihre Tabellenzeile.
            const emailsendAt =
              res.emailsend_at || moment().format(this.dateFormat);
            this.$set(item, 'emailsend_at', emailsendAt);
            this.$set(item, 'emailsend', true);
            this.$emit('sent', {
              original: this.original,
              invoice: item,
              emailsend_at: emailsendAt
            });
          } else if (res.status === 'error') {
            const reasonText = this.sendReasonText(res.reason);
            result = {
              variant: 'danger',
              text:
                'Rechnung nicht versendet.' + (reasonText ? ' ' + reasonText : '')
            };
          } else {
            result = {
              variant: 'danger',
              text: 'Der Versand wurde nicht bestätigt. Bitte „Rechnung verschickt am“ nach dem Neuladen der Seite prüfen.'
            };
          }

          // Antwort zu einer inzwischen anders geöffneten Rechnung nicht anzeigen
          if (this.current !== item) return;
          this.sendResult = result;

          if (result.variant === 'primary') {
            setTimeout(() => {
              if (this.sendResult === result) this.sendResult = null;
            }, 3000);
          }
        })
        .catch((error) => {
          this.$toast.open({
            message:
              (error && error.message) ||
              'Rechnung konnte nicht versendet werden',
            type: 'error',
            position: 'bottom',
            duration: 2500
          });
          console.error('Error sending invoice:', error);
        })
        .finally(() => {
          if (this.current === item) this.sending = false;
        });
    }
  }
};
</script>
